/**
 * Barra flotante de acciones bulk para el Radar (Sprint 9 · 3).
 *
 * Aparece sticky al fondo del viewport cuando el usuario tiene 1+ cards
 * seleccionadas vía sus checkboxes. Ofrece 4 acciones según el contexto:
 *
 * - **Marcar como favoritas (N)** — siempre disponible.
 * - **Descartar (N)** — siempre disponible.
 * - **Crear expediente** — solo cuando N === 1 (un expediente vincula
 *   a UNA convocatoria) y el plan admite otro expediente activo.
 * - **Limpiar selección** — reset inmediato.
 *
 * Las acciones bulk se ejecutan vía `setOpportunityAction` por cada id
 * en paralelo (Promise.all). Si algún call falla, el resto sigue. Al
 * terminar, se invalida la queryKey 'opportunities' y se limpia la
 * selección.
 *
 * **Errores de "Crear expediente" (issue #568).** Hasta entonces la
 * mutación no mostraba su error en ningún lado: el clic fallaba en
 * silencio y el usuario no sabía por qué no pasaba nada. Ahora la barra
 * tiene un slot arriba de los botones — el 402 del cupo va con enlace a
 * `/membresia`, el resto como línea de error.
 *
 * **Diseño visual:**
 * - Sticky bottom, centered horizontally.
 * - Background brand-700 con texto blanco — alta visibilidad sin ruido.
 * - Conteo de seleccionadas a la izquierda, acciones a la derecha.
 * - Botón "Crear expediente" deshabilitado con tooltip cuando N !== 1 o
 *   cuando el cupo del plan está agotado.
 *
 * **A11y:**
 * - role="region" + aria-label.
 * - Estado disabled de "Crear expediente" lleva aria-disabled + title
 *   con razón ("Selecciona exactamente una convocatoria" / cupo agotado).
 * - Los errores van como `role="alert"`.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { CupoExpedientesNota } from "@/components/CupoExpedientesNota";
import { track } from "@/lib/analytics";
import { ApiError, createProject, setOpportunityAction, type OpportunityDTO } from "@/lib/api";
import { myPlanQueryKey } from "@/lib/hooks/useActiveProjectsQuota";

/** Cuenta cuántas promesas fallaron en un batch de acciones bulk. */
function countFailed(results: PromiseSettledResult<unknown>[]): number {
  return results.filter((r) => r.status === "rejected").length;
}

export interface BulkActionsBarProps {
  /** IDs seleccionados — orden no importa, el contenido sí. */
  selectedIds: string[];
  /** Mapa id → opp para acceder a title cuando se crea expediente. */
  opportunitiesById: Map<string, OpportunityDTO>;
  /** DANE del muni activo — necesario para los endpoints. */
  dane: string;
  /**
   * El plan ya no admite otro expediente activo (issue #568). Llega como
   * prop desde el Radar, que ya consultó el plan: la barra no necesita
   * su propia query, igual que no resuelve el `dane` por su cuenta.
   */
  cupoAgotado?: boolean;
  /** Limpia la selección (lo invoca el padre tras éxito). */
  onClear: () => void;
}

export function BulkActionsBar({
  selectedIds,
  opportunitiesById,
  dane,
  cupoAgotado = false,
  onClear,
}: BulkActionsBarProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const count = selectedIds.length;

  const favoriteMutation = useMutation({
    mutationFn: async () => {
      // Aplicamos en paralelo. Promise.allSettled para no abortar al
      // primer fallo — algunos pueden estar ya favoritos (idempotente)
      // o tener errores transitorios.
      const results = await Promise.allSettled(
        selectedIds.map((id) =>
          setOpportunityAction({
            opportunityId: id,
            action: "favorite",
            dane,
            apply: true,
          }),
        ),
      );
      return { total: selectedIds.length, failed: countFailed(results) };
    },
    onSuccess: ({ total, failed }) => {
      track({
        event_name: "opportunity_favorited",
        // `bulk` + `failed_count` nos dan visibilidad de fallos parciales
        // sin necesidad de un sistema de toast todavía.
        properties: { count: total, failed_count: failed, bulk: true },
      });
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      onClear();
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(
        selectedIds.map((id) =>
          setOpportunityAction({
            opportunityId: id,
            action: "dismiss",
            dane,
            apply: true,
          }),
        ),
      );
      return { total: selectedIds.length, failed: countFailed(results) };
    },
    onSuccess: ({ total, failed }) => {
      track({
        event_name: "opportunity_dismissed",
        properties: { count: total, failed_count: failed, bulk: true },
      });
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      onClear();
    },
  });

  const expedienteMutation = useMutation({
    mutationFn: async () => {
      // Solo se invoca cuando count === 1.
      const id = selectedIds[0];
      if (id === undefined) throw new Error("No selection");
      const opp = opportunitiesById.get(id);
      if (!opp) throw new Error("Opportunity not found in current page");
      return createProject({ opportunity_id: opp.id, name: opp.title.slice(0, 200) }, dane);
    },
    onSuccess: (project) => {
      // El expediente nuevo consume un cupo del plan (issue #568) y ocupa
      // la convocatoria (issue #541): la tarjeta tiene que pasar a "Ver
      // expediente".
      queryClient.invalidateQueries({ queryKey: myPlanQueryKey(dane) });
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      onClear();
      navigate(`/expedientes/${project.id}`);
    },
    onError: (error) => {
      // 409 = la convocatoria ya tiene un expediente vivo (el Radar estaba
      // desactualizado). Al refrescar, su tarjeta lo enlaza.
      if (error instanceof ApiError && error.status === 409) {
        queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      }
      // 402 = el cupo se agotó con el Radar abierto (issue #738): el plan y
      // el listado cacheados siguen ofreciendo crear.
      if (error instanceof ApiError && error.status === 402) {
        queryClient.invalidateQueries({ queryKey: myPlanQueryKey(dane) });
        queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      }
    },
  });

  // `POST /projects` sólo devuelve 402 por cupo agotado, así que el status
  // alcanza para distinguirlo; el texto lo pone el backend.
  const errorExpediente = expedienteMutation.error;
  const cupoError = errorExpediente instanceof ApiError && errorExpediente.status === 402;
  // Un expediente vivo por convocatoria (issue #541): si la seleccionada ya
  // tiene uno, crear otro lo rechazaría el backend.
  const yaTieneExpediente =
    count === 1 && !!opportunitiesById.get(selectedIds[0] ?? "")?.existing_project_id;

  const isPending =
    favoriteMutation.isPending || dismissMutation.isPending || expedienteMutation.isPending;

  if (count === 0) return null;

  return (
    <div
      role="region"
      aria-label="Acciones en lote"
      data-testid="bulk-actions-bar"
      className="fixed bottom-4 left-1/2 z-30 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col gap-2 rounded-xl bg-slate-900 px-4 py-3 text-white shadow-2xl ring-1 ring-slate-700"
    >
      {/* Hasta el issue #568 el error de crear expediente no se mostraba en
          ningún lado: el clic fallaba en silencio. El 402 del cupo lleva
          enlace a /membresia; el resto va como línea suelta. */}
      {cupoError && <CupoExpedientesNota tono="error" mensaje={errorExpediente.message} />}
      {errorExpediente && !cupoError && (
        <p
          role="alert"
          data-testid="bulk-expediente-error"
          className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800"
        >
          {errorExpediente instanceof Error
            ? errorExpediente.message
            : "No pudimos crear el expediente. Inténtalo de nuevo."}
        </p>
      )}

      <div className="flex items-center gap-3">
        <span data-testid="bulk-count" className="text-sm font-semibold">
          {count} {count === 1 ? "seleccionada" : "seleccionadas"}
        </span>
        <span aria-hidden="true" className="h-5 w-px bg-slate-600"></span>

        <button
          type="button"
          onClick={() => favoriteMutation.mutate()}
          disabled={isPending}
          data-testid="bulk-favorite"
          className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold ring-1 ring-slate-700 hover:bg-slate-700 disabled:opacity-50"
        >
          ♥ Favoritas ({count})
        </button>

        <button
          type="button"
          onClick={() => dismissMutation.mutate()}
          disabled={isPending}
          data-testid="bulk-dismiss"
          className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold ring-1 ring-slate-700 hover:bg-slate-700 disabled:opacity-50"
        >
          ✘ Descartar ({count})
        </button>

        <button
          type="button"
          onClick={() => expedienteMutation.mutate()}
          disabled={isPending || count !== 1 || cupoAgotado || yaTieneExpediente}
          aria-disabled={count !== 1 || cupoAgotado || yaTieneExpediente}
          title={
            yaTieneExpediente
              ? "Ya tienes un expediente de esta convocatoria. Ábrelo con 'Ver expediente' en su tarjeta."
              : cupoAgotado
                ? "Tu plan no admite otro expediente activo. Cierra o rechaza uno para liberar un cupo."
                : count !== 1
                  ? "Selecciona exactamente 1 convocatoria para crear un expediente"
                  : "Crear expediente con la convocatoria seleccionada"
          }
          data-testid="bulk-expediente"
          className="rounded-md bg-cta-600 px-3 py-1.5 text-xs font-semibold hover:bg-cta-700 disabled:opacity-40"
        >
          Crear expediente
        </button>

        <span aria-hidden="true" className="h-5 w-px bg-slate-600"></span>

        <button
          type="button"
          onClick={onClear}
          disabled={isPending}
          data-testid="bulk-clear"
          className="text-xs font-medium text-slate-300 hover:text-white disabled:opacity-50"
        >
          Limpiar
        </button>
      </div>
    </div>
  );
}
